import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.js';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase.js';
import { toast } from 'react-hot-toast';

function GeneralSettings() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    notifications: {
      streamStart: true,
      mentions: true,
      teamUpdates: true,
      tournamentUpdates: true
    },
    privacy: 'public',
    theme: 'dark',
    language: 'en'
  });

  useEffect(() => {
    const fetchSettings = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.settings) {
            setSettings(prev => ({
              ...prev,
              ...data.settings
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [currentUser]);

  const handleNotificationChange = (event) => {
    const { name, checked } = event.target;
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [name]: checked
      }
    }));
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setLoading(true);
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { settings });
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-harmony-orange"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="text-harmony-white">
      <div className="space-y-6">
        {/* Notifications Section */}
        <div className="bg-harmony-blue rounded-lg p-4 mb-6">
          <h3 className="text-xl font-medium mb-4">Notifications</h3>
          
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                id="streamStart"
                type="checkbox"
                name="streamStart"
                checked={settings.notifications.streamStart}
                onChange={handleNotificationChange}
                className="h-5 w-5 mr-3 accent-harmony-orange"
              />
              <label htmlFor="streamStart" className="text-harmony-white">
                Stream Start Notifications
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="mentions"
                type="checkbox"
                name="mentions"
                checked={settings.notifications.mentions}
                onChange={handleNotificationChange}
                className="h-5 w-5 mr-3 accent-harmony-orange"
              />
              <label htmlFor="mentions" className="text-harmony-white">
                Mentions
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="teamUpdates"
                type="checkbox"
                name="teamUpdates"
                checked={settings.notifications.teamUpdates}
                onChange={handleNotificationChange}
                className="h-5 w-5 mr-3 accent-harmony-orange"
              />
              <label htmlFor="teamUpdates" className="text-harmony-white">
                Team Updates
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="tournamentUpdates"
                type="checkbox"
                name="tournamentUpdates"
                checked={settings.notifications.tournamentUpdates}
                onChange={handleNotificationChange}
                className="h-5 w-5 mr-3 accent-harmony-orange"
              />
              <label htmlFor="tournamentUpdates" className="text-harmony-white">
                Tournament Updates
              </label>
            </div>
          </div>
        </div>
        
        {/* Account Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="privacy" className="block text-harmony-white/70 mb-2">
              Privacy
            </label>
            <select
              id="privacy"
              name="privacy"
              value={settings.privacy}
              onChange={handleSelectChange}
              className="w-full bg-harmony-blue border border-harmony-white/10 text-harmony-white rounded px-3 py-2"
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="friends">Friends Only</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="theme" className="block text-harmony-white/70 mb-2">
              Theme
            </label>
            <select
              id="theme"
              name="theme"
              value={settings.theme}
              onChange={handleSelectChange}
              className="w-full bg-harmony-blue border border-harmony-white/10 text-harmony-white rounded px-3 py-2"
            >
              <option value="dark">Dark</option>
              <option value="light">Light</option>
              <option value="system">System (Use device theme)</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="language" className="block text-harmony-white/70 mb-2">
              Language
            </label>
            <select
              id="language"
              name="language"
              value={settings.language}
              onChange={handleSelectChange}
              className="w-full bg-harmony-blue border border-harmony-white/10 text-harmony-white rounded px-3 py-2"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
            </select>
          </div>
        </div>
        
        <div className="pt-4">
          <button
            type="submit"
            className="bg-harmony-orange hover:bg-harmony-orange/90 text-harmony-white px-4 py-2 rounded font-medium disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </form>
  );
}

export default GeneralSettings; 